import classNames from 'classnames';
import { forwardRef, useEffect, useRef } from 'react';
import { MARKER_STATUS } from 'src/interfaces/enums';
import { IMarkerOptimist } from 'src/interfaces/Marker';
import NotesListItem from '../../components/NotesListItem';
import styles from './styles.module.scss';

export interface INotesListProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  locale?: string;
  notes: IMarkerOptimist[];
  millisecondsToOpeningAnimation?: number;
  onTimeSelect: (time: number) => void;
  onDelete: (note: IMarkerOptimist) => void;
  onEdit: (note: IMarkerOptimist) => void;
  onRetry: (note: IMarkerOptimist) => void;
}

type NoteItemElements = Record<string, HTMLDivElement | null>;

const NotesList = forwardRef<HTMLDivElement, INotesListProps>(
  (
    { className, notes, locale, millisecondsToOpeningAnimation = 0, onDelete, onEdit, onTimeSelect, onRetry, ...rest },
    ref,
  ) => {
    const notesItemsRef = useRef<NoteItemElements>({} as NoteItemElements);

    useEffect(() => {
      const note = notes.find((item) => item.status === MARKER_STATUS.PENDING);
      const item = notesItemsRef?.current?.[`${note?.id || ''}`];

      const time = setTimeout(() => {
        item && item.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, millisecondsToOpeningAnimation);

      return () => {
        clearTimeout(time);
      };
    }, [notes, ref, millisecondsToOpeningAnimation]);

    return (
      <div role='list' ref={ref} className={classNames(styles.container, className)} {...rest}>
        {notes.map((item) => (
          <NotesListItem
            key={item.id}
            ref={(element) => (notesItemsRef.current[`${item.id}`] = element)}
            note={item}
            locale={locale}
            onEdit={() => onEdit(item)}
            onDelete={() => {
              onDelete(item);
            }}
            onTimeSelect={() => {
              onTimeSelect(item.time);
            }}
            onRetry={() => {
              onRetry(item);
            }}
          />
        ))}
      </div>
    );
  },
);

NotesList.displayName = 'NotesList';

export default NotesList;
